import { DateTime } from 'luxon';
import Decimal from 'decimal.js';
import { Entity, ItemLinkEntity, LinkableEntity } from '../entity';
import { AccountingSystem, Job } from '../job';
import { ActivityEntityName, Address, CodeStatus, CodeType, CodeUoM, EmbeddedCode, SiteTiny } from '../shared';
import { TaxRate } from '../taxrate';
import * as Enumerable from 'linq';
import { ContactEntity, NotifiableContactEntity } from '../contact';
import { Company } from '../tenant';
import { LinkRelationship } from '../../enums';
import { InvoiceUtility } from '../../utilities/invoice.utility';
import { Work } from '../work';

export enum PurchaseOrderStatus {
    Draft = 'Draft',

    /** @deprecated never used */
    Submitted = 'Submitted',

    PendingApproval = 'Pending Approval',

    ChangesRequired = 'Changes Required',

    AwaitingDelivery = 'Awaiting Delivery',

    /** @deprecated use Approved */
    Authorised = 'Authorised',
    Approved = 'Approved',

    /** @deprecated use Billed */
    Reconciled = 'Reconciled',
    Billed = 'Billed',

    /** @deprecated use Billed */
    Error = 'Error',

    /** @deprecated use Pending Approval */
    SubmittedForApproval = 'Submitted For Approval',

    /** @deprecated use Cancelled */
    Void = 'Void',
    Cancelled = 'Cancelled',
}

export enum PurchaseOrderViewType {
    Job = 'Job',
    Global = 'Global'
}

export enum QuickbookTaxInclusiveOption {
    Exclusive = 'TaxExcluded',
    Inclusive = 'TaxInclusive',
    NoTax = 'NotApplicable'
}

export enum TaxInclusiveOption {
    Exclusive = 'Tax Exclusive',
    Inclusive = 'Tax Inclusive',
    NoTax = 'No Tax'
}

export type PurchaseOrderDeliverOptions = 'pickup' | 'company' | 'job' | 'other';

export type PurchaseOrderKind = ActivityEntityName.Job | ActivityEntityName.Item
    | ActivityEntityName.Work | ActivityEntityName.WorkItem;

export interface PurchaseOrder extends LinkableEntity {

    poNumber?: string;
    poDate?: Date;
    deliveryDate?: Date;
    deliveryInstructions?: string;
    billingCompany: PurchaseOrderBillingCompany;
    reference?: string;
    supplier?: PurchaseOrderSupplier;
    comment?: string;
    status: PurchaseOrderStatus;
    lines?: PurchaseOrderLine[];
    job?: Entity;
    createdBy?: Entity;
    owner?: Entity;

    deliveryOptions?: PurchaseOrderDeliverOptions;
    deliveryAddress?: DeliveryAddress;
    taxInclusiveOption: TaxInclusiveOption;
    subTotal?: number;
    total?: number;
    tax?: number;

    createdAt?: Date;
    updatedAt?: Date;

    theme?: Entity;

    priceBook?: Entity;

    pdf?: string;

    changesRequiredNote?: string;

    /**
     * @description
     * - If the purchase order created from Job-Item, it will be **Item** kind.
     * - If the purchase order created from Job > PO tab, it will be **Job** kind.
     * - etc.
     */
    kind?: PurchaseOrderKind;

    /** @deprecated */
    supplierMarkup?: number;

    /** @deprecated */
    taxRate?: TaxRate;

    /** @deprecated */
    chargeable?: boolean;

    /** @deprecated */
    accounting?: PurchaseOrderAccounting; // Purchase Order Accounting is belong to company therefore if company have many accounting, the integration will in Array for tracking one by one
}

export interface PurchaseOrderAccounting {
    type: AccountingSystem;
    poAccountingId: string;
    deepLink?: string;
    success?: boolean;
    error?: any;
}

export interface PurchaseOrderLine {
    _id?: string
    name?: string;
    description?: string;
    quantity?: number;
    price?: number;
    customPrice?: boolean;

    tax: number;
    subTotal: number;
    total: number;
    location?: string;
    notes?: string;
    codeStatus?: CodeStatus;
    uom?: string;
    codeUoM?: CodeUoM;
    itemLink?: ItemLinkEntity;
    // billLink?: ItemLinkEntity;

    glCode?: string;
    taxRate?: TaxRate;
    type?: CodeType;
    createFromCode?: boolean;
    canOverrideDesc?: boolean;
}

export interface DeliveryAddress {
    name: string;
    company: string;
    phone: string;
    address: Address;
}

export interface PurchaseOrderJob extends Entity {
    jobNumber: string;
    address: Address;
}

export interface PurchaseOrderSupplier extends Entity {
    site?: SiteTiny;
    contact?: NotifiableContactEntity;
    address: Address;
    phone: string;
    email: string;
    markupPercent?: number;
    contactName?: string; // I think we r not use contactName any more. It is for old purchase-order.
}

export interface PurchaseOrderBillingCompany extends Entity {
    address: Address;
    phone: string;
    email: string;
    taxRate: number;
    taxId: string;
}

export interface PurchaseOrderSummary {
    _id: string;
    poDate: Date;
    poNumber: string;
    billingCompany: Entity;
    supplier: Entity;
    createdBy: Entity;
    total: number;
    subTotal: number;
    job?: PurchaseOrderJob;
    status: PurchaseOrderStatus;
}

export interface PurchaseOrderCreateResponse {
    message?: string;
    accountingId: string;
    deepLink?: string;
    createdDate?: Date;
    success?: boolean;
}

function buildPurchaseOrderLineFromEmbeddedCode(taxRate: TaxRate, code: EmbeddedCode, itemLink?: ItemLinkEntity): PurchaseOrderLine {

    // const price = 0 ?? 0;
    // const taxRateValue = taxRate?.rate || 0;
    // const quantity = code.quantity || 1;
    // const subTotal = new Decimal(quantity * price).toDecimalPlaces(4).toNumber();
    // const tax = new Decimal(subTotal * taxRateValue).toDecimalPlaces(4).toNumber();
    // const total = new Decimal(subTotal + tax).toDecimalPlaces(4).toNumber();
    const price = new Decimal((code.buyPrice ?? 0) * (code.codeUoM?.conversionRate ?? 1)).toDP(4).toNumber();

    const line: PurchaseOrderLine = {
        _id: code._id,
        name: code.name,
        description: code.description,
        quantity: code.quantity,
        customPrice: true,
        price,
        subTotal: 0,
        total: 0,
        tax: 0,
        location: code.location,
        notes: code.note,
        codeStatus: code.status,
        uom: code.uom,
        codeUoM: code.codeUoM,
        glCode: null,
        taxRate: taxRate,
        type: code.type,
        createFromCode: true,
        canOverrideDesc: !!code.canOverrideDesc,
        itemLink,
    };

    return line;
}

export function buildPurchaseOrder(company: Company, user: ContactEntity): PurchaseOrder {

    let billingCompany: PurchaseOrderBillingCompany;

    if (company) {
        billingCompany = {
            _id: company._id,
            name: company.name,
            address: company.address,
            phone: company.phone,
            email: company.email,
            taxRate: company.taxRate,
            taxId: company.taxId
        };
    }

    return {
        _id: null,
        poNumber: null,
        poDate: DateTime.now().setZone(company?.address?.timezone ?? 'utc').toJSDate(),
        billingCompany: billingCompany,
        reference: null,
        supplier: null,
        supplierMarkup: 0,
        comment: null,
        status: PurchaseOrderStatus.Draft,
        lines: [],
        createdBy: user,
        chargeable: true,
        taxRate: null,
        deliveryOptions: 'pickup',
        deliveryAddress: null,
        taxInclusiveOption: TaxInclusiveOption.Exclusive,
        subTotal: 0,
        total: 0,
        tax: 0,
        links: [],
        theme: null
    };
}

export function buildPurchaseOrderFromJob(job: Job, company: Company, taxRate: TaxRate, user: ContactEntity, codesIds: string[] = []): PurchaseOrder {

    const template = buildPurchaseOrder(company, user);

    let lines: PurchaseOrderLine[] = [];

    if (codesIds?.length) {
        lines = Enumerable
            .from(job.workOrders)
            .selectMany(wo => {


                const codes = Enumerable
                    .from(wo.codes)
                    .where(c => codesIds?.length
                        ? codesIds.includes(c._id)
                        : true
                    )
                    .select(c => {
                        const itemLink: ItemLinkEntity = {
                            _id: wo._id,
                            name: wo.workOrderNumber,
                            lineId: c._id,
                        };

                        return buildPurchaseOrderLineFromEmbeddedCode(taxRate, c, itemLink)
                    })
                    .toArray();
                return codes;
            })
            .toArray();
    }

    // I did feel shame for using 'any' here. But too scared to change the InvoiceUtility 😂
    const formattedLines = InvoiceUtility.formatLinesPrice(lines as any);
    const { subTotal, tax, total } = InvoiceUtility.getInvoiceLineTotal(formattedLines, taxRate, template.taxInclusiveOption);

    return <PurchaseOrder>{
        ...template,
        job: {
            _id: job._id,
            name: job.jobNumber
        },
        poDate: DateTime.now().setZone(job?.site?.address?.timezone).toJSDate(),
        lines: lines,
        subTotal,
        tax,
        total,
        kind: lines.length > 0
            ? ActivityEntityName.Item
            : ActivityEntityName.Job,
        links: [
            {
                _id: job._id,
                type: ActivityEntityName.Job,
                name: job.jobNumber,
                relationship: LinkRelationship.From
            }
        ]
    }
}

export function buildPurchaseOrderFromWork(work: Work, company: Company, taxRate: TaxRate, user: ContactEntity, codesIds: string[] = []): PurchaseOrder {

    const template = buildPurchaseOrder(company, user);

    let lines: PurchaseOrderLine[] = [];

    if (codesIds?.length) {
        // TODO: implement logic
    }

    // I did feel shame for using 'any' here. But too scared to change the InvoiceUtility 😂
    const formattedLines = InvoiceUtility.formatLinesPrice(lines as any);
    const { subTotal, tax, total } = InvoiceUtility.getInvoiceLineTotal(formattedLines, taxRate, template.taxInclusiveOption);

    return <PurchaseOrder>{
        ...template,
        poDate: DateTime.now().setZone(work?.site?.address?.timezone).toJSDate(),
        lines: lines,
        subTotal,
        tax,
        total,
        kind: ActivityEntityName.Work,
        links: [
            {
                _id: work._id,
                type: ActivityEntityName.Work,
                name: work.workNumber,
                relationship: LinkRelationship.From
            }
        ]
    }
}

export function isPurchaseOrderKindItem(po: PurchaseOrder) {
    return po?.kind === ActivityEntityName.Item || Enumerable.from(po?.lines).any(x => !!x.itemLink?._id);
}

export enum PurchaseOrderCommandId {
    Approve = 'Approve',
    Submit = 'Submit',
    Bill = 'Bill',
    MarkAsSent = 'MarkAsSent',
    SendPo = 'SendPo',
    GenerateBill = 'GenerateBill',
    Cancel = 'Cancel',
    CancelAndCancelLinkedItems = 'CancelAndCancelLinkedItems',
    NewTemplate = 'NewTemplate',
    GetAttachment = 'GetAttachment',
    ChangesRequired = 'ChangesRequired',
}

export type PurchaseOrderCommandStatus = PurchaseOrderCommandId.Approve | PurchaseOrderCommandId.Submit | PurchaseOrderCommandId.Bill | PurchaseOrderCommandId.MarkAsSent | PurchaseOrderCommandId.SendPo | PurchaseOrderCommandId.Cancel;

export type PurchaseOrderCommandRequest =
    {
        commandId: PurchaseOrderCommandId.Approve;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Submit;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Bill;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.MarkAsSent;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.SendPo;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Cancel;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.GenerateBill;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.NewTemplate;
        // TODO: should use entityId and entityType instead of jobId, workId
        data: { _id: string, jobId?: string, workId?: string, codeIds?: string[] };
    } |
    {
        commandId: PurchaseOrderCommandId.GetAttachment;
        data: { _id: string };
    };
